import React from "react";
import { Document, Page, Path, StyleSheet, Svg, Text, View } from "@react-pdf/renderer";
import {
  commonStyles,
  formatDate,
  formatPhoneNumber,
  isValidSummary,
  parseHtmlToText,
  renderBulletPoints,
} from "../Services/ReactPDFService.jsx";

// Professional Blue Template - Elegant design with profile photo, blue accents, and clean layout
const styles = StyleSheet.create({
  ...commonStyles,
  page: {
    ...commonStyles.page,
    backgroundColor: "#ffffff",
    paddingTop: 30,
    paddingBottom: 30,
    paddingHorizontal: 40,
  },
  // Header with centered name
  headerContainer: {
    alignItems: "center",
    marginBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: "#3b5998",
    paddingBottom: 8,
  },
  // Name with elegant italic styling
  name: {
    fontSize: 26,
    fontWeight: 400,
    color: "#2c3e50",
    marginBottom: 25,
    letterSpacing: 0.5,
  },
  // Contact row with icons
  contactRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: 20,
  },
  contactItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
  },
  contactIcon: {
    width: 12,
    height: 12,
  },
  contactText: {
    fontSize: 10,
    color: "#555555",
  },
  // Summary text
  summaryText: {
    fontSize: 10,
    lineHeight: 1.5,
    color: "#444444",
    textAlign: "justify",
    marginBottom: 10,
    marginTop: 8,
  },
  // Section styling with blue header - line above and below title
  sectionContainer: {
    marginBottom: 8,
  },
  sectionTitleContainer: {
    marginBottom: 6,
  },
  sectionLine: {
    height: 0.5,
    backgroundColor: "#3b5998",
    width: "100%",
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#3b5998",
    textAlign: "center",
    paddingVertical: 3,
  },
  // Experience items - two column layout (job info left, bullets right 80%)
  experienceItem: {
    flexDirection: "row",
    marginBottom: 8,
  },
  experienceLeft: {
    width: "25%",
    paddingRight: 10,
  },
  experienceRight: {
    width: "75%",
  },
  // Job header row (title, company, date, location in one line)
  jobHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 6,
  },
  jobHeaderLeft: {
    flex: 1,
  },
  jobHeaderRight: {
    alignItems: "flex-end",
  },
  dateText: {
    fontSize: 9,
    color: "#666666",
    marginBottom: 2,
    textAlign: "left",
  },
  locationText: {
    fontSize: 9,
    color: "#666666",
    textAlign: "left",
  },
  jobTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#2c3e50",
  },
  companyName: {
    fontSize: 10,
    color: "#666666",
    fontStyle: "italic",
    marginBottom: 2,
  },
  bulletPoint: {
    fontSize: 10,
    marginBottom: 2,
    paddingLeft: 10,
    lineHeight: 1.4,
    color: "#444444",
  },
  // Education items - two column layout (degree left, date right)
  educationItem: {
    flexDirection: "row",
    marginBottom: 6,
    justifyContent: "space-between",
  },
  educationLeft: {
    width: "70%",
    paddingRight: 10,
  },
  educationRight: {
    width: "30%",
    alignItems: "flex-end",
  },
  educationDateText: {
    fontSize: 9,
    color: "#666666",
    marginBottom: 2,
    textAlign: "right",
  },
  educationLocationText: {
    fontSize: 9,
    color: "#666666",
    textAlign: "right",
  },
  degree: {
    fontSize: 11,
    fontWeight: 700,
    color: "#2c3e50",
  },
  school: {
    fontSize: 10,
    color: "#666666",
    fontStyle: "italic",
  },
  // Skills section - inline with bullet separators (left aligned)
  skillsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  skillText: {
    fontSize: 10,
    color: "#2c3e50",
  },
  skillSeparator: {
    fontSize: 10,
    color: "#2c3e50",
    marginHorizontal: 8,
  },
  // Languages section - badge style
  languagesContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 3,
  },
  languageBadge: {
    backgroundColor: "#3b5998",
    paddingVertical: 3,
    paddingHorizontal: 10,
    borderRadius: 2,
  },
  languageText: {
    fontSize: 10,
    color: "#ffffff",
    fontWeight: 500,
  },
  // Projects section
  projectItem: {
    marginBottom: 8,
  },
  projectTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#2c3e50",
  },
  projectTech: {
    fontSize: 9,
    color: "#666666",
    fontStyle: "italic",
    marginTop: 1,
  },
  // Certifications section
  certificationItem: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 6,
  },
  certName: {
    fontSize: 10,
    fontWeight: 700,
    color: "#2c3e50",
  },
  certAuthority: {
    fontSize: 10,
    color: "#666666",
    marginLeft: 5,
  },
});

const ProfessionalBlueTemplate = ({ resumeData, customStyles = {}, customSectionOrder = null }) => {
  const {
    personalInfo = {},
    workExperience = [],
    education = [],
    skills = {},
    projects = [],
    certifications = [],
    achievements = [],
    languages = [],
    awards = [],
    volunteering = [],
    interests = [],
    industryExpertise = [],
    publications = [],
    references = [],
    customSections = [],
    sectionTitles = {},
    summary = "",
  } = resumeData || {};

  // Create dynamic styles based on customizations
  const createDynamicStyles = (baseStyles, customizations) => {
    const hasCustomizations = customizations && Object.keys(customizations).length > 0;
    if (!hasCustomizations) return baseStyles;

    const { fontSize, sectionSpacing, titleColor, subtitleColor, fontFamily } = customizations;
    const customStyleOverrides = {};

    if (fontFamily) {
      customStyleOverrides.page = { ...baseStyles.page, fontFamily };
    }

    if (fontSize) {
      customStyleOverrides.name = { ...baseStyles.name, fontSize: fontSize * 2.2 };
      customStyleOverrides.sectionTitle = { ...baseStyles.sectionTitle, fontSize: fontSize * 0.95 };
      customStyleOverrides.jobTitle = { ...baseStyles.jobTitle, fontSize: fontSize * 0.85 };
      customStyleOverrides.degree = { ...baseStyles.degree, fontSize: fontSize * 0.85 };
      customStyleOverrides.summaryText = { ...baseStyles.summaryText, fontSize: fontSize * 0.8 };
      customStyleOverrides.bulletPoint = { ...baseStyles.bulletPoint, fontSize: fontSize * 0.8 };
    }

    if (sectionSpacing) {
      customStyleOverrides.sectionContainer = { ...baseStyles.sectionContainer, marginBottom: sectionSpacing };
    }

    if (titleColor) {
      customStyleOverrides.sectionTitle = {
        ...(customStyleOverrides.sectionTitle || baseStyles.sectionTitle),
        color: titleColor,
      };
      customStyleOverrides.sectionLine = {
        ...baseStyles.sectionLine,
        backgroundColor: titleColor,
      };
      customStyleOverrides.languageBadge = {
        ...baseStyles.languageBadge,
        backgroundColor: titleColor,
      };
    }

    return StyleSheet.create({ ...baseStyles, ...customStyleOverrides });
  };

  const mergedStyles = createDynamicStyles(styles, customStyles);

  // Default section titles
  const defaultTitles = {
    summary: 'Profile', workExperience: 'Experience', education: 'Education', skills: 'Skills',
    projects: 'Projects', certifications: 'Certifications', achievements: 'Achievements',
    languages: 'Languages', awards: 'Awards', volunteering: 'Volunteering', interests: 'Interests',
    industryExpertise: 'Industry Expertise', publications: 'Publications', references: 'References',
  };
  const getSectionTitle = (key) => sectionTitles[key] || defaultTitles[key];

  // Default section order
  const defaultSectionOrder = ["summary", "workExperience", "education", "skills", "projects", "certifications", "languages", "achievements", "awards", "volunteering", "interests", "industryExpertise", "publications", "references"];

  // Build final section order - include custom sections
  const customSectionIds = customSections.filter(cs => cs.id && cs.title && cs.items?.length > 0).map(cs => cs.id);
  const baseSectionOrder = customSectionOrder || defaultSectionOrder;
  const finalSectionOrder = [...baseSectionOrder, ...customSectionIds.filter(id => !baseSectionOrder.includes(id))];

  // Combine all skills for display
  const allSkills = (() => {
    if (Array.isArray(skills)) return skills;
    if (skills.list && Array.isArray(skills.list) && skills.list.length > 0) return skills.list;
    const combinedSkills = [
      ...(skills.technical || []),
      ...(skills.soft || []),
      ...(skills.tools || []),
      ...(skills.other || []),
    ];
    if (combinedSkills.length > 0) return combinedSkills;
    if (skills.categorized) {
      return [
        ...(skills.categorized.technical || []),
        ...(skills.categorized.soft || []),
        ...(skills.categorized.tools || []),
        ...(skills.categorized.other || []),
      ];
    }
    return [];
  })();

  const getSkillName = (skill) => {
    if (typeof skill === "string") return skill;
    if (skill && typeof skill === "object") return skill.name || skill.skill || "";
    return "";
  };

  // Helper to get language name from string or object
  const getLanguageName = (lang) => {
    if (typeof lang === "string") return lang;
    if (lang && typeof lang === "object") return lang.name || lang.language || "";
    return "";
  };

  // Helper to get interest name from string or object
  const getInterestName = (interest) => {
    if (typeof interest === "string") return interest;
    if (interest && typeof interest === "object") return interest.name || interest.title || "";
    return "";
  };

  // Section components
  const sectionComponents = {
    summary: () =>
      summary &&
      isValidSummary(summary) && (
        <Text style={mergedStyles.summaryText}>{parseHtmlToText(summary)}</Text>
      ),

    workExperience: () =>
      workExperience.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionTitleContainer}>
            <View style={mergedStyles.sectionLine} />
            <Text style={mergedStyles.sectionTitle}>Professional Experience</Text>
            <View style={mergedStyles.sectionLine} />
          </View>
          {workExperience.map((job, index) => (
            <View key={index} style={mergedStyles.experienceItem}>
              {/* LEFT side (20%) - Job title, company, date, location */}
              <View style={mergedStyles.experienceLeft}>
                <Text style={mergedStyles.jobTitle}>{job.jobTitle},</Text>
                <Text style={mergedStyles.companyName}>{job.company}</Text>
                <Text style={mergedStyles.dateText}>
                  {formatDate(job.startDate)} – {" "}
                  {job.isCurrentJob || job.endDate === "Present" || job.endDate === "Current" || !job.endDate
                    ? "Present"
                    : formatDate(job.endDate)}
                </Text>
                {job.location && <Text style={mergedStyles.locationText}>{job.location}</Text>}
              </View>
              {/* RIGHT side (80%) - Bullet points */}
              <View style={mergedStyles.experienceRight}>
                {job.description && renderBulletPoints(parseHtmlToText(job.description), mergedStyles)}
              </View>
            </View>
          ))}
        </View>
      ),

    education: () =>
      education.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionTitleContainer}>
            <View style={mergedStyles.sectionLine} />
            <Text style={mergedStyles.sectionTitle}>Education</Text>
            <View style={mergedStyles.sectionLine} />
          </View>
          {education.map((edu, index) => (
            <View key={index} style={mergedStyles.educationItem}>
              {/* LEFT side - Degree and School */}
              <View style={mergedStyles.educationLeft}>
                <Text style={mergedStyles.degree}>
                  {edu.degree}
                  {edu.fieldOfStudy ? ` (${edu.fieldOfStudy})` : ""},
                </Text>
                <Text style={mergedStyles.school}>{edu.school}</Text>
              </View>
              {/* RIGHT side - Date and Location */}
              <View style={mergedStyles.educationRight}>
                <Text style={mergedStyles.educationDateText}>
                  {formatDate(edu.startDate)} – {edu.endDate === "Present" ? "Present" : formatDate(edu.endDate)}
                </Text>
                {edu.location && <Text style={mergedStyles.educationLocationText}>{edu.location}</Text>}
              </View>
            </View>
          ))}
        </View>
      ),

    skills: () =>
      allSkills.length > 0 && (
        <View style={mergedStyles.sectionContainer} wrap={false}>
          <View style={mergedStyles.sectionTitleContainer}>
            <View style={mergedStyles.sectionLine} />
            <Text style={mergedStyles.sectionTitle}>Skills</Text>
            <View style={mergedStyles.sectionLine} />
          </View>
          <View style={mergedStyles.skillsContainer}>
            {allSkills.slice(0, 12).map((skill, index) => (
              <React.Fragment key={index}>
                <Text style={mergedStyles.skillText}>{getSkillName(skill)}</Text>
                {index < Math.min(allSkills.length, 12) - 1 && (
                  <Text style={mergedStyles.skillSeparator}>•</Text>
                )}
              </React.Fragment>
            ))}
          </View>
        </View>
      ),

    languages: () =>
      languages &&
      languages.length > 0 && (
        <View style={mergedStyles.sectionContainer} wrap={false}>
          <View style={mergedStyles.sectionTitleContainer}>
            <View style={mergedStyles.sectionLine} />
            <Text style={mergedStyles.sectionTitle}>Languages</Text>
            <View style={mergedStyles.sectionLine} />
          </View>
          <View style={mergedStyles.languagesContainer}>
            {languages.map((lang, index) => (
              <View key={index} style={mergedStyles.languageBadge}>
                <Text style={mergedStyles.languageText}>{getLanguageName(lang)}</Text>
              </View>
            ))}
          </View>
        </View>
      ),

    projects: () =>
      projects.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionTitleContainer}>
            <View style={mergedStyles.sectionLine} />
            <Text style={mergedStyles.sectionTitle}>Projects</Text>
            <View style={mergedStyles.sectionLine} />
          </View>
          {projects.map((project, index) => (
            <View key={index} style={mergedStyles.projectItem}>
              <Text style={mergedStyles.projectTitle}>{project.name}</Text>
              {project.technologies && (
                <Text style={mergedStyles.projectTech}>Technologies: {project.technologies}</Text>
              )}
              {project.description && (
                <View style={{ marginTop: 4 }}>
                  {renderBulletPoints(parseHtmlToText(project.description), mergedStyles)}
                </View>
              )}
            </View>
          ))}
        </View>
      ),

    certifications: () =>
      certifications.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionTitleContainer}>
            <View style={mergedStyles.sectionLine} />
            <Text style={mergedStyles.sectionTitle}>{getSectionTitle('certifications')}</Text>
            <View style={mergedStyles.sectionLine} />
          </View>
          {certifications.map((cert, index) => (
            <View key={index} style={mergedStyles.certificationItem}>
              <Text style={mergedStyles.certName}>{cert.name}</Text>
              <Text style={mergedStyles.certAuthority}>
                – {cert.authority || cert.issuer}{cert.date && ` (${formatDate(cert.date)})`}
              </Text>
            </View>
          ))}
        </View>
      ),

    achievements: () => achievements.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <View style={mergedStyles.sectionTitleContainer}>
          <View style={mergedStyles.sectionLine} />
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('achievements')}</Text>
          <View style={mergedStyles.sectionLine} />
        </View>
        {achievements.map((achievement, index) => (
          <View key={index} style={mergedStyles.certificationItem}>
            {achievement.description && <Text style={mergedStyles.certName}>{parseHtmlToText(achievement.description)}</Text>}
          </View>
        ))}
      </View>
    ),

    awards: () => awards.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <View style={mergedStyles.sectionTitleContainer}>
          <View style={mergedStyles.sectionLine} />
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('awards')}</Text>
          <View style={mergedStyles.sectionLine} />
        </View>
        {awards.map((award, index) => (
          <View key={index} style={mergedStyles.certificationItem}>
            <Text style={mergedStyles.certName}>{award.title || award.name}</Text>
            <Text style={mergedStyles.certAuthority}>– {award.issuer}{award.date && ` (${formatDate(award.date)})`}</Text>
          </View>
        ))}
      </View>
    ),

    volunteering: () => volunteering.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <View style={mergedStyles.sectionTitleContainer}>
          <View style={mergedStyles.sectionLine} />
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('volunteering')}</Text>
          <View style={mergedStyles.sectionLine} />
        </View>
        {volunteering.map((vol, index) => (
          <View key={index} style={mergedStyles.experienceItem}>
            <View style={mergedStyles.experienceHeader}>
              <Text style={mergedStyles.jobTitle}>{vol.role}</Text>
              {(vol.startDate || vol.endDate) && <Text style={mergedStyles.dateRange}>{formatDate(vol.startDate)} – {vol.isCurrentRole ? "Present" : formatDate(vol.endDate)}</Text>}
            </View>
            <Text style={mergedStyles.companyName}>{vol.organization}</Text>
            {vol.description && <View style={{ marginTop: 4 }}>{renderBulletPoints(parseHtmlToText(vol.description), mergedStyles)}</View>}
          </View>
        ))}
      </View>
    ),

    interests: () => interests.length > 0 && (
      <View style={mergedStyles.sectionContainer} wrap={false}>
        <View style={mergedStyles.sectionTitleContainer}>
          <View style={mergedStyles.sectionLine} />
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('interests')}</Text>
          <View style={mergedStyles.sectionLine} />
        </View>
        <View style={mergedStyles.languagesContainer}>
          {interests.map((interest, index) => (
            <View key={index} style={mergedStyles.languageBadge}>
              <Text style={mergedStyles.languageText}>{getInterestName(interest)}</Text>
            </View>
          ))}
        </View>
      </View>
    ),

    industryExpertise: () => industryExpertise.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <View style={mergedStyles.sectionTitleContainer}>
          <View style={mergedStyles.sectionLine} />
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('industryExpertise')}</Text>
          <View style={mergedStyles.sectionLine} />
        </View>
        {industryExpertise.map((exp, index) => (
          <View key={index} style={mergedStyles.certificationItem}>
            <Text style={mergedStyles.certName}>{exp.name}</Text>
            <Text style={mergedStyles.certAuthority}>– {exp.proficiency}{exp.yearsOfExperience && ` (${exp.yearsOfExperience} years)`}</Text>
          </View>
        ))}
      </View>
    ),

    publications: () => publications.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <View style={mergedStyles.sectionTitleContainer}>
          <View style={mergedStyles.sectionLine} />
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('publications')}</Text>
          <View style={mergedStyles.sectionLine} />
        </View>
        {publications.map((pub, index) => (
          <View key={index} style={mergedStyles.certificationItem}>
            <Text style={mergedStyles.certName}>{pub.title}</Text>
            <Text style={mergedStyles.certAuthority}>– {pub.publisher}{pub.date && ` (${formatDate(pub.date)})`}</Text>
          </View>
        ))}
      </View>
    ),

    references: () => references.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <View style={mergedStyles.sectionTitleContainer}>
          <View style={mergedStyles.sectionLine} />
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('references')}</Text>
          <View style={mergedStyles.sectionLine} />
        </View>
        {references.map((ref, index) => (
          <View key={index} style={mergedStyles.certificationItem}>
            <Text style={mergedStyles.certName}>{ref.name}</Text>
            <Text style={mergedStyles.certAuthority}>– {ref.position}{ref.company && ` at ${ref.company}`}{ref.email && ` • ${ref.email}`}</Text>
          </View>
        ))}
      </View>
    ),

    // Dynamic custom sections
    ...customSections.reduce((acc, customSection) => {
      if (customSection.id && customSection.title && customSection.items?.length > 0) {
        acc[customSection.id] = () => (
          <View style={mergedStyles.sectionContainer}>
            <View style={mergedStyles.sectionTitleContainer}>
              <View style={mergedStyles.sectionLine} />
              <Text style={mergedStyles.sectionTitle}>{customSection.title}</Text>
              <View style={mergedStyles.sectionLine} />
            </View>
            {customSection.items.map((item, itemIndex) => (
              <View key={itemIndex} style={mergedStyles.certificationItem}>
                {item.content && <Text style={mergedStyles.certName}>{parseHtmlToText(item.content)}</Text>}
              </View>
            ))}
          </View>
        );
      }
      return acc;
    }, {}),
  };

  return (
    <Document>
      <Page size="A4" style={mergedStyles.page} wrap={true}>
        {/* Header Section */}
        <View style={mergedStyles.headerContainer}>
          {/* Name */}
          <Text style={mergedStyles.name}>
            {personalInfo.firstName} {personalInfo.lastName}
          </Text>

          {/* Contact Info Row */}
          <View style={mergedStyles.contactRow}>
            {/* Location */}
            {personalInfo.location && (
              <View style={mergedStyles.contactItem}>
                <Svg style={mergedStyles.contactIcon} viewBox="0 0 24 24">
                  <Path
                    d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                    fill="#3b5998"
                  />
                </Svg>
                <Text style={mergedStyles.contactText}>{personalInfo.location}</Text>
              </View>
            )}

            {/* Email */}
            {personalInfo.email && (
              <View style={mergedStyles.contactItem}>
                <Svg style={mergedStyles.contactIcon} viewBox="0 0 24 24">
                  <Path
                    d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
                    fill="#3b5998"
                  />
                </Svg>
                <Text style={mergedStyles.contactText}>{personalInfo.email}</Text>
              </View>
            )}

            {/* Phone */}
            {personalInfo.phone && (
              <View style={mergedStyles.contactItem}>
                <Svg style={mergedStyles.contactIcon} viewBox="0 0 24 24">
                  <Path
                    d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
                    fill="#3b5998"
                  />
                </Svg>
                <Text style={mergedStyles.contactText}>{formatPhoneNumber(personalInfo.phone)}</Text>
              </View>
            )}
          </View>
        </View>

        {/* Main Content */}
        <View>
          {finalSectionOrder.map((sectionId, index) => {
            const SectionComponent = sectionComponents[sectionId];
            const content = SectionComponent ? SectionComponent() : null;
            return content ? <View key={`${sectionId}-${index}`} wrap={true}>{content}</View> : null;
          })}
        </View>
      </Page>
    </Document>
  );
};

export default ProfessionalBlueTemplate;
